import { effect, ErrorHandler, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { IUser } from '@lb/api/mylb/interfaces/iuser.interface';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { AccountStateService } from '@lb/state/account-state.service';
import { UserContextService } from '@lb/state/user-context.service';
import { ANALYTICS_APP } from './analytics/analytics-app.token';
import { AppInsightsAnalyticsProvider } from './analytics/app-insights-analytics-provider';
import { IAnalyticsProvider } from './analytics/analytics-provider.interface';
import { ClarityAnalyticsProvider } from './analytics/clarity-analytics-provider';
import { routePattern } from './analytics/route-pattern';

interface AnalyticsIdentity {
	userId: string;
	role: string;
	friendlyName: string;
}

// Query params worth capturing as page context (admin impersonation).
const TRACKED_QUERY_PARAMS = ['learnerID', 'instructorID'];

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);
	private readonly _router = inject(Router);
	private readonly _accountState = inject(AccountStateService);
	private readonly _userContext = inject(UserContextService);
	/**
	 * Which app this session is in (learner / instructor / admin), provided per app via
	 * `provideAnalyticsApp`. Optional so a bare TestBed (or any app that hasn't wired it) still
	 * constructs — analytics then runs without the `app` tag rather than throwing.
	 */
	private readonly _app = inject(ANALYTICS_APP, { optional: true });
	// Every active provider (Clarity, App Insights) receives the same identity / view-context /
	// route-entity signals so a new destination is added without touching any call site.
	private readonly _providers: IAnalyticsProvider[] = [];
	private _identity: AnalyticsIdentity | null = null;
	private _currentPageId: string | null = null;

	constructor() {
		const analyticsConfig = this._appSettings.analytics;

		// Clarity — gated by the master analytics switch, a project id (present per environment), and
		// a per-app opt-out. All three apps share one Clarity project, so excludeApps is the only way
		// to keep an individual app out of session recording; App Insights below is deliberately left
		// running for excluded apps so error and page telemetry is unaffected.
		const clarityProjectId = analyticsConfig?.clarity?.projectId;
		const clarityExcluded = !!this._app && (analyticsConfig?.clarity?.excludeApps ?? []).includes(this._app);
		if (analyticsConfig?.enabled && clarityProjectId && !clarityExcluded) {
			const clarity = new ClarityAnalyticsProvider();
			clarity.initialize(clarityProjectId);
			this._providers.push(clarity);
		}

		// App Insights — gated by the master analytics switch AND a connection string, mirroring
		// Clarity. Requiring `enabled` keeps instrumentation off in dev even though the dev
		// app-settings.json still carries a connection string (the switch is the single source of
		// truth for whether analytics runs at all).
		const appInsightsConnectionString = analyticsConfig?.appInsights?.connectionString;
		if (analyticsConfig?.enabled && appInsightsConnectionString) {
			// provideAppInsights registers ApplicationinsightsAngularpluginErrorService as the app's
			// ErrorHandler. Resolve it once so its singleton is constructed before the plugin
			// initializes, keeping trackException wiring independent of construction order.
			inject(ErrorHandler);
			// Chain a SEPARATE, plain ErrorHandler so uncaught errors are still logged to the console in
			// addition to reaching App Insights. This must never be the plugin's own error service: that
			// service is the app's registered ErrorHandler, so adding it to its own forward-list makes
			// handleError call itself for every uncaught error — unbounded recursion that queues a
			// trackException per frame and floods the ingestion endpoint until the tab freezes.
			const consoleErrorHandler = new ErrorHandler();
			const appInsights = new AppInsightsAnalyticsProvider(this._router, consoleErrorHandler);
			appInsights.initialize(appInsightsConnectionString);
			this._providers.push(appInsights);
		}

		if (this._hasProviders()) {
			this._applyApp();

			// Tags are page-scoped, so re-apply identity and stamp the route's entity
			// IDs on every navigation. This keeps who/what visible on each page of a
			// recording and makes sessions filterable by userId, enrollmentId, etc.
			this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
				const url = (event as NavigationEnd).urlAfterRedirects;
				// Use the matched route pattern (e.g. /enrollments/:id/lessons/:lessonId) as identify's
				// custom-page-id so per-entity URLs group together. Set before _applyIdentity so it
				// goes out with identify.
				this._currentPageId = this._routePattern();
				this._applyIdentity();
				this._applyApp();
				this._applyViewContext();
				this._tagRouteContext(url);
			});

			// Re-apply identity + viewing context the moment they change (impersonation start/stop,
			// guardian / institution user switch) — not just on navigation — so recordings always
			// attribute to the real driver and show whose data is on screen.
			effect(() => {
				this._applyIdentity();
				this._applyViewContext();
			});
		}
	}

	public identifyUser(user: IUser | null): void {
		if (!user || !this._hasProviders()) {
			return;
		}

		this._identity = {
			userId: String(user.userID),
			role: user.roleName,
			friendlyName: this._buildFriendlyName(user)
		};
		this._applyIdentity();
	}

	public setTag(key: string, value: string | string[]): void {
		this._eachProvider((provider) => provider.setTag(key, value));
	}

	/**
	 * Tags the entity IDs the user is currently viewing so recordings are filterable by them.
	 * Call from data-load points (e.g. an enrollment/lesson detail resolving) to capture related
	 * IDs the URL doesn't carry (learnerId, instructorId, programId, ...). Nullish values are skipped.
	 */
	public setContext(context: Record<string, string | number | boolean | null | undefined>): void {
		if (!this._hasProviders()) {
			return;
		}
		for (const [key, value] of Object.entries(context)) {
			if (value !== undefined && value !== null) {
				this._eachProvider((provider) => provider.setTag(key, String(value)));
			}
		}
	}

	public trackEvent(eventName: string, properties?: Record<string, unknown>): void {
		this._eachProvider((provider) => provider.trackEvent(eventName, properties));
	}

	private _hasProviders(): boolean {
		return this._providers.length > 0;
	}

	private _eachProvider(action: (provider: IAnalyticsProvider) => void): void {
		for (const provider of this._providers) {
			action(provider);
		}
	}

	private _applyIdentity(): void {
		// Read impersonation state unconditionally so the reactive effect tracks it.
		const adminUserId = this._accountState.isImpersonating() ? this._accountState.originalAdminUserID()?.userID : undefined;

		if (!this._identity || !this._hasProviders()) {
			return;
		}

		// During impersonation the account identity is swapped to the target, but we attribute the
		// session to the REAL admin so at-a-glance session data doesn't look like the impersonated
		// learner/instructor performed the actions. The viewedUserId tag still records who was viewed.
		const userId = adminUserId != null ? String(adminUserId) : this._identity.userId;
		const friendlyName = adminUserId != null ? String(adminUserId) : this._identity.friendlyName;
		const role = this._identity.role;

		this._eachProvider((provider) => provider.identifyUser(userId, role, friendlyName, this._currentPageId ?? undefined));
	}

	/**
	 * Tags which app the session is in (learner / instructor / admin) from ANALYTICS_APP. Role != app
	 * (an admin can impersonate into the learner/instructor app), so this tag is the only reliable way
	 * to filter a shared Clarity project by app. Re-applied per navigation because tags are page-scoped.
	 */
	private _applyApp(): void {
		if (!this._app || !this._hasProviders()) {
			return;
		}
		const app = this._app;
		this._eachProvider((provider) => provider.setTag('app', app));
	}

	/**
	 * Tags how the session is viewing user data so recordings distinguish who is *driving* from
	 * whose data is *on screen*:
	 *  - impersonation: an admin viewing a learner/instructor (account identity is swapped to the
	 *    target, `originalAdminUser` holds the admin).
	 *  - associated: a guardian-style related user viewing a learner's pages.
	 *  - institution: an institution contact viewing a learner's pages.
	 *  - self: viewing their own data (viewing == viewed).
	 * Page-scoped, so re-applied per navigation and reactively on change.
	 */
	private _applyViewContext(): void {
		if (!this._hasProviders()) {
			return;
		}

		const loggedInUserId = this._accountState.userID();
		if (loggedInUserId == null) {
			return;
		}

		// Impersonation swaps the account identity to the target, so the logged-in user IS the
		// viewed user and the real driver is the original admin.
		if (this._accountState.isImpersonating()) {
			const adminUserId = this._accountState.originalAdminUserID()?.userID;
			this._setViewContext('impersonation', adminUserId, loggedInUserId);
			return;
		}

		// Guardian / institution keep the logged-in identity and view another user via UserContext.
		const mode = this._userContext.switchMode();
		if (this._userContext.isViewingOtherUser() && (mode === 'guardian' || mode === 'institution')) {
			const viewType = mode === 'guardian' ? 'associated' : 'institution';
			this._setViewContext(viewType, loggedInUserId, this._userContext.viewingUserID());
			return;
		}

		// Not viewing anyone else — viewing == viewed keeps the tags unambiguous (no stale value).
		this._setViewContext('self', loggedInUserId, loggedInUserId);
	}

	private _setViewContext(viewType: string, viewingUserId?: number, viewedUserId?: number): void {
		if (!this._hasProviders()) {
			return;
		}
		this._eachProvider((provider) => {
			provider.setTag('viewType', viewType);
			if (viewingUserId != null) {
				provider.setTag('viewingUserId', String(viewingUserId));
			}
			if (viewedUserId != null) {
				provider.setTag('viewedUserId', String(viewedUserId));
			}
		});
	}

	private _buildFriendlyName(user: IUser): string {
		// const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
		return user.userID.toString();
	}

	/**
	 * Derives entity IDs from the URL structure and records them as tags. Any
	 * `<entity>/<number>` pair in the path becomes `<entity>Id = <number>`
	 * (e.g. `enrollments/123/lessons/45` -> enrollmentId=123, lessonId=45), which
	 * handles the mix of `:id` / `:lessonId` route params without a lookup table.
	 */
	private _tagRouteContext(url: string): void {
		if (!this._hasProviders()) {
			return;
		}

		const [path, query] = url.split('?');
		const segments = path.split('/').filter(Boolean);

		for (let i = 0; i < segments.length - 1; i++) {
			const key = segments[i];
			const value = segments[i + 1];
			if (/^\d+$/.test(value) && !/^\d+$/.test(key)) {
				this._eachProvider((provider) => provider.setTag(this._toIdTagKey(key), value));
			}
		}

		if (query) {
			const params = new URLSearchParams(query);
			for (const param of TRACKED_QUERY_PARAMS) {
				const value = params.get(param);
				if (value) {
					this._eachProvider((provider) => provider.setTag(param, value));
				}
			}
		}
	}

	private _toIdTagKey(segment: string): string {
		const singular = segment.replace(/s$/, '');
		const camel = singular.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
		return `${camel}Id`;
	}

	private _routePattern(): string {
		return routePattern(this._router);
	}
}
